import React, { createContext, useContext } from "react";

/**
 * A single SEO link impression captured during SSR.
 *
 * The fields populated here mirror the legacy emitter
 * `com.indeed.mobileweb.log.MobileEventLogger#logLinkGraph` so RN-emitted rows
 * are bit-for-bit comparable to legacy `seoLinkGraph` Imhotep rows. The host
 * (jobseeker-frontend) enriches each entry with `country`, `lang`, `ctk`, and
 * `bot_tag` at emit time before writing to the `seoLinkGraph` LogRepo type via
 * `@indeed/logrepo`.
 *
 * See JSSEO-1948 for context.
 */
export interface SeoLinkGraphEntry {
  /** URL of the rendered link (e.g. similar-jobs SERP, company page). */
  canonicalurl: string;
  /** URL of the ViewJob page that contains the link. */
  pagecanonicalurl: string;
  /**
   * Legacy `SeoSurface.java` description string, e.g. `"viewjob similar job"`.
   * Must match the legacy strings exactly so existing IQL queries continue to
   * work against the combined dataset.
   */
  surface: string;
  /** Zero-indexed position of the link within its surface section. */
  index: number;
  /**
   * RN render context (`MOBILE_STANDALONE`, `DESKTOP_STANDALONE`,
   * `DESKTOP_EMBEDDED`). Disambiguates RN-emitted vs legacy-emitted rows
   * during cutover and validation.
   */
  rnViewJobDisplay: string;
  /** ViewJob's job key — useful for joining to other tables. */
  viewjob_jk: string;
  /**
   * Mobile ViewJob tracking key. Sourced from
   * `viewjobOneGraphModel.mobvjtk` (which itself is the `mobtk` value
   * from the page model). Mapped to the legacy Avro field `vjtk` for
   * schema parity with `MobileEventLogger#logLinkGraph`.
   *
   * Empty string when not available. Other legacy Avro fields
   * (`serptk`, `ql`, `noindex`, `noindexReason`) are enriched by the
   * host (jobseeker-frontend) at emit time — see JSF-604.
   */
  vjtk: string;
}

export interface SeoLinkGraphSink {
  push(entry: SeoLinkGraphEntry): void;
}

export interface SeoLinkGraphCollector extends SeoLinkGraphSink {
  /**
   * Returns all entries collected so far and clears the buffer. Call once
   * after `renderToPipeableStream` resolves and before returning the SSR
   * payload to the host.
   */
  drain(): SeoLinkGraphEntry[];
}

const NOOP_SINK: SeoLinkGraphSink = {
  push: () => {},
};

const SeoLinkGraphCollectorContext = createContext<SeoLinkGraphSink>(NOOP_SINK);

/**
 * Create a new per-render collector. Each SSR `render()` call MUST create its
 * own collector — the worker pool / main-thread render path may handle
 * concurrent requests, and a shared collector would cross-contaminate them.
 *
 * Entries are deduplicated by `(surface, index, canonicalurl)` so that
 * concurrent / Suspense re-renders within a single SSR pass do not produce
 * duplicate rows.
 */
export function createSeoLinkGraphCollector(): SeoLinkGraphCollector {
  const entries: SeoLinkGraphEntry[] = [];
  const seen = new Set<string>();

  return {
    push(entry) {
      const key = `${entry.surface}|${entry.index}|${entry.canonicalurl}`;
      if (seen.has(key)) return;
      seen.add(key);
      entries.push(entry);
    },
    drain() {
      const drained = entries.slice();
      entries.length = 0;
      seen.clear();
      return drained;
    },
  };
}

export interface SeoLinkGraphCollectorProviderProps {
  value: SeoLinkGraphSink;
  children: React.ReactNode;
}

/**
 * SSR-only provider. Mounted exclusively by `react-native/web/viewjob/index.server.ts`
 * around the rendered `<App>` tree.
 */
export const SeoLinkGraphCollectorProvider: React.FC<SeoLinkGraphCollectorProviderProps> = ({
  value,
  children,
}) => {
  return (
    <SeoLinkGraphCollectorContext.Provider value={value}>
      {children}
    </SeoLinkGraphCollectorContext.Provider>
  );
};

/**
 * Returns the active sink. Outside an SSR provider (CSR / native), returns a
 * no-op sink so call sites can call `push()` unconditionally without runtime
 * checks.
 */
export function useSeoLinkGraphSink(): SeoLinkGraphSink {
  return useContext(SeoLinkGraphCollectorContext);
}

/**
 * Maps an `AdditionalLinks` section `testID` to the legacy `SeoSurface.java`
 * description string used by the existing `seoLinkGraph` Imhotep index.
 *
 * Returns `null` for unknown sections — callers should skip emission in that
 * case rather than fall back to a default, to avoid accidentally polluting the
 * `surface` dimension with unmapped values.
 *
 * Source of truth for these strings:
 * `code.corp.indeed.com/jobsearch/indeedmobile` →
 * `src/main/java/com/indeed/mobileweb/seo/SeoSurface.java`.
 */
export function surfaceForAdditionalLinksTestID(ID: string): string | null {
  switch (ID) {
    case "company-and-salary-information":
      return "viewjob company and salary links";
    case "similar-jobs":
      return "viewjob similar job";
    case "similar-categories":
      return "viewjob similar category";
    case "career-guide-articles":
      return "viewjob career guide article";
    case "similar-jobs-nearby":
      return "viewjob nearby location links";
    default:
      return null;
  }
}
